// Cabin geometry of the aerialways, shared by the game (aerialway.js) and the
// dashboard's ride preview (objpreview.js → /api/objpreview.js): ONE source,
// so the preview is the cabin the players see.
import * as THREE from 'three';

export const GLASS_RGB = [0.34, 0.44, 0.52];   // solid tinted "glass" band (1 draw call)
export const DARK = [0.16, 0.17, 0.19];
export const STEEL = [0.6, 0.62, 0.65];
export const CONCRETE = [0.71, 0.69, 0.65];

export function clampRgb(v, dflt) {
  if (!Array.isArray(v) || v.length < 3) return dflt;
  return v.slice(0, 3).map((c) => Math.min(1, Math.max(0, +c || 0)));
}

// -------------------------------------------------------- geometry helpers
// merge parts [{geo, color:[r,g,b]}] into one vertex-colored geometry
export function mergeParts(parts) {
  const pos = [], nor = [], col = [];
  for (const { geo, color } of parts) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const p = g.getAttribute('position'), n = g.getAttribute('normal');
    for (let i = 0; i < p.count; i++) {
      pos.push(p.getX(i), p.getY(i), p.getZ(i));
      nor.push(n.getX(i), n.getY(i), n.getZ(i));
      col.push(color[0], color[1], color[2]);
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  out.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  out.computeBoundingSphere();
  return out;
}

export function box(w, h, d, x, y, z, ry = 0, rz = 0, rx = 0) {
  const g = new THREE.BoxGeometry(w, h, d);
  if (rx) g.rotateX(rx);
  if (rz) g.rotateZ(rz);
  if (ry) g.rotateY(ry);
  g.translate(x, y, z);
  return g;
}

// ------------------------------------------------------------ cabin shapes
// Origin at FLOOR CENTER, forward = +Z (station zones beam riders to
// position + 0.3, same as the funicular cars).
export function pendelCabinParts(prof, drop) {
  const body = clampRgb(prof && prof.body_rgb, [0.75, 0.12, 0.13]);
  const roof = clampRgb(prof && prof.roof_rgb, body.map((c) => c * 0.8));
  const stripe = prof && prof.stripe === false ? null
    : clampRgb(prof && prof.stripe_rgb, [0.95, 0.95, 0.95]);
  const split = Math.min(1.6, Math.max(0.7,
    (prof && +prof.two_tone_split) || 1.05));
  const P = [];
  P.push({ geo: box(2.5, 0.18, 3.6, 0, 0.09, 0), color: DARK });          // floor
  // body skirt (4 sides) up to the waistline
  P.push({ geo: box(2.5, split, 0.08, 0, split / 2 + 0.18, 1.76), color: body });
  P.push({ geo: box(2.5, split, 0.08, 0, split / 2 + 0.18, -1.76), color: body });
  P.push({ geo: box(0.08, split, 3.6, 1.21, split / 2 + 0.18, 0), color: body });
  P.push({ geo: box(0.08, split, 3.6, -1.21, split / 2 + 0.18, 0), color: body });
  if (stripe) {
    for (const [x, z, w, d] of [[0, 1.79, 2.5, 0.06], [0, -1.79, 2.5, 0.06],
      [1.24, 0, 0.06, 3.6], [-1.24, 0, 0.06, 3.6]]) {
      P.push({ geo: box(w, 0.14, d, x, split + 0.2, z), color: stripe });
    }
  }
  // window band up to the roof, corner pillars in body color
  const wh = 2.1 - split;
  P.push({ geo: box(2.42, wh, 3.52, 0, split + 0.24 + wh / 2, 0),
    color: clampRgb(prof && prof.glass_rgb, GLASS_RGB) });
  for (const [x, z] of [[1.19, 1.72], [-1.19, 1.72], [1.19, -1.72], [-1.19, -1.72],
    [1.19, 0], [-1.19, 0]]) {
    P.push({ geo: box(0.1, wh + 0.1, 0.1, x, split + 0.24 + wh / 2, z), color: body });
  }
  P.push({ geo: box(2.6, 0.16, 3.72, 0, 2.44, 0), color: roof });          // roof
  // A-frame hanger up to the carriage riding the track cable
  const armL = drop - 2.5;
  for (const s of [1, -1]) {
    P.push({ geo: box(0.12, armL + 0.5, 0.18, s * 0.45, 2.5 + armL / 2, 0,
      0, s * -0.17), color: DARK });
  }
  P.push({ geo: box(0.34, 0.4, 2.2, 0, drop - 0.2, 0), color: DARK });     // carriage
  for (const z of [-0.9, -0.3, 0.3, 0.9]) {
    P.push({ geo: box(0.1, 0.34, 0.34, 0, drop + 0.05, z), color: STEEL }); // wheels
  }
  return P;
}

export function gondolaCabinParts(prof, drop) {
  const body = clampRgb(prof && prof.body_rgb, [0.85, 0.86, 0.88]);
  const roof = clampRgb(prof && prof.roof_rgb, [0.72, 0.1, 0.12]);
  const P = [];
  P.push({ geo: box(1.6, 0.14, 1.6, 0, 0.07, 0), color: DARK });           // floor
  P.push({ geo: box(1.8, 0.72, 1.9, 0, 0.5, 0), color: body });            // skirt
  P.push({ geo: box(1.72, 0.8, 1.82, 0, 1.26, 0),
    color: clampRgb(prof && prof.glass_rgb, GLASS_RGB) });                  // windows
  for (const [x, z] of [[0.84, 0.89], [-0.84, 0.89], [0.84, -0.89], [-0.84, -0.89]]) {
    P.push({ geo: box(0.09, 0.86, 0.09, x, 1.26, z), color: body });        // pillars
  }
  P.push({ geo: box(1.84, 0.16, 1.94, 0, 1.74, 0), color: roof });
  P.push({ geo: box(1.3, 0.18, 1.4, 0, 1.9, 0), color: roof });            // roof taper
  P.push({ geo: box(0.12, drop - 1.9, 0.12, 0, (drop + 1.9) / 2, 0), color: DARK });
  P.push({ geo: box(0.22, 0.3, 0.6, 0, drop - 0.12, 0), color: DARK });    // grip
  return P;
}
